
import { HelphubStyleConfig, QuestThemeData } from "../CustomHelpHub.type";

export const WrapLinkText = (
  text: string,
  idx: number,
  styleConfig?: HelphubStyleConfig,
  QuestThemeData?: QuestThemeData
) => {
  const processText = (input: string) => {
    const segments: Array<{
      type: "text" | "link" | "break" | "bold" | "italic" | "paragraph";
      content?: string;
      text?: string;
      url?: string;
    }> = [];

    const paragraphs = input.split(/\n\s*\n/);

    paragraphs.forEach((paragraph, pIndex) => {
      if (pIndex > 0) {
        segments.push({ type: "paragraph" });
      }

      // Split paragraph into lines
      const lines = paragraph.split(/\n/);

      lines.forEach((line, lineIndex) => {
        if (lineIndex > 0) {
          segments.push({ type: "break" });
        }

        let currentPosition = 0;
        const length = line.length;

        while (currentPosition < length) {
          // Check for markdown-style links [text](url)
          const markdownLinkMatch = line
            ?.slice(currentPosition)
            ?.match(/\[([^\]]+)\]\(([^)]+)\)/);
          // Check for plain URLs
          const urlMatch = line
            ?.slice(currentPosition)
            .match(/https?:\/\/[^\s<>[\]()]+/);
          // Check for bold text
          const boldMatch = line
            ?.slice(currentPosition)
            .match(/\*\*([^*]+)\*\*/);
          // Check for italic text
          const italicMatch = line?.slice(currentPosition).match(/_([^_]+)_/);

          if (markdownLinkMatch && markdownLinkMatch.index === 0) {
            segments.push({
              type: "link",
              text: markdownLinkMatch[1],
              url: markdownLinkMatch[2],
            });
            currentPosition += markdownLinkMatch[0].length;
          } else if (urlMatch && urlMatch.index === 0) {
            const contextBefore = line?.slice(
              Math.max(0, currentPosition - 30),
              currentPosition
            );
            const { text, url } = processUrl(urlMatch[0], contextBefore);
            segments.push({
              type: "link",
              text,
              url,
            });
            currentPosition += urlMatch[0].length;
          } else if (boldMatch && boldMatch.index === 0) {
            segments.push({
              type: "bold",
              content: boldMatch[1],
            });
            currentPosition += boldMatch[0].length;
          } else if (italicMatch && italicMatch.index === 0) {
            segments.push({
              type: "italic",
              content: italicMatch[1],
            });
            currentPosition += italicMatch[0].length;
          } else {
            const nextSpecialChar = line
              ?.slice(currentPosition)
              .match(/[\[\*_]|https?:\/\//);
            const textEndPosition =
              nextSpecialChar && nextSpecialChar.index
                ? currentPosition + nextSpecialChar.index
                : length;

            if (textEndPosition > currentPosition) {
              segments.push({
                type: "text",
                content: line?.slice(currentPosition, textEndPosition),
              });
            }
            currentPosition = textEndPosition;

            if (!nextSpecialChar) break;
          }
        }
      });
    });

    return segments;
  };

  const processUrl = (
    url: string,
    contextBefore: string = ""
  ): { text: string; url: string } => {
    // Remove any trailing punctuation from URLs
    url = url.replace(/[.,;!?)]+$/, "");

    // Professional Social Networks
    if (url.includes("linkedin.com")) return { text: "LinkedIn Profile", url };
    if (url.includes("xing.com")) return { text: "Xing Profile", url };
    if (url.includes("behance.net")) return { text: "Behance Portfolio", url };
    if (url.includes("dribbble.com"))
      return { text: "Dribbble Portfolio", url };
    if (url.includes("researchgate.net"))
      return { text: "ResearchGate Profile", url };
    if (url.includes("academia.edu")) return { text: "Academia Profile", url };

    // Social Media Platforms
    if (url.includes("twitter.com") || url.includes("x.com"))
      return { text: "Twitter Profile", url };
    if (url.includes("instagram.com"))
      return { text: "Instagram Profile", url };
    if (url.includes("facebook.com")) return { text: "Facebook Profile", url };
    if (url.includes("tiktok.com")) return { text: "TikTok Profile", url };
    if (url.includes("snapchat.com")) return { text: "Snapchat Profile", url };
    if (url.includes("pinterest.com"))
      return { text: "Pinterest Profile", url };
    if (url.includes("reddit.com/u/")) return { text: "Reddit Profile", url };
    if (url.includes("weibo.com")) return { text: "Weibo Profile", url };
    if (url.includes("vk.com")) return { text: "VK Profile", url };

    // Development & Tech
    if (url.includes("github.com")) return { text: "GitHub Repository", url };
    if (url.includes("gitlab.com")) return { text: "GitLab Repository", url };
    if (url.includes("bitbucket.org"))
      return { text: "Bitbucket Repository", url };
    if (url.includes("stackoverflow.com"))
      return { text: "Stack Overflow", url };
    if (url.includes("dev.to")) return { text: "Dev.to Article", url };
    if (url.includes("hashnode.com")) return { text: "Hashnode Blog", url };
    if (url.includes("npm.com")) return { text: "NPM Package", url };
    if (url.includes("pypi.org")) return { text: "PyPI Package", url };

    // Meeting & Calendar
    if (url.includes("calendly.com")) return { text: "Book a Call", url };
    if (url.includes("zoom.us")) return { text: "Join Zoom", url };
    if (url.includes("teams.microsoft.com"))
      return { text: "Join Teams Meeting", url };
    if (url.includes("meet.google.com"))
      return { text: "Join Google Meet", url };
    if (url.includes("webex.com")) return { text: "Join Webex", url };
    if (url.includes("whereby.com")) return { text: "Join Whereby", url };

    // Productivity & Documents
    if (url.includes("notion.so")) return { text: "Notion Page", url };
    if (url.includes("airtable.com")) return { text: "Airtable Base", url };
    if (url.includes("trello.com")) return { text: "Trello Board", url };
    if (url.includes("asana.com")) return { text: "Asana Project", url };
    if (url.includes("clickup.com")) return { text: "ClickUp Task", url };
    if (url.includes("monday.com")) return { text: "Monday Board", url };
    if (url.includes("miro.com")) return { text: "Miro Board", url };
    if (url.includes("figma.com")) return { text: "Figma Design", url };
    if (url.includes("sketch.com")) return { text: "Sketch Design", url };
    if (url.includes("invisionapp.com"))
      return { text: "InVision Prototype", url };

    // Google Workspace
    if (url.includes("docs.google.com/document"))
      return { text: "Google Doc", url };
    if (url.includes("docs.google.com/spreadsheets"))
      return { text: "Google Sheet", url };
    if (url.includes("docs.google.com/presentation"))
      return { text: "Google Slides", url };
    if (url.includes("docs.google.com/forms"))
      return { text: "Google Form", url };
    if (url.includes("drive.google.com")) return { text: "Google Drive", url };

    // Microsoft 365
    if (url.includes("office.com/word")) return { text: "Word Online", url };
    if (url.includes("office.com/excel")) return { text: "Excel Online", url };
    if (url.includes("office.com/powerpoint"))
      return { text: "PowerPoint Online", url };
    if (url.includes("onedrive.live.com"))
      return { text: "OneDrive File", url };
    if (url.includes("sharepoint.com")) return { text: "SharePoint", url };

    // Communication
    if (url.includes("slack.com")) return { text: "Slack Channel", url };
    if (url.includes("discord.com") || url.includes("discord.gg"))
      return { text: "Discord Server", url };
    if (url.includes("telegram.me") || url.includes("t.me"))
      return { text: "Telegram", url };
    if (url.includes("whatsapp.com")) return { text: "WhatsApp", url };
    if (url.includes("messenger.com")) return { text: "Messenger", url };
    if (url.includes("skype.com")) return { text: "Skype", url };

    // Content & Blogging
    if (url.includes("medium.com")) return { text: "Medium Article", url };
    if (url.includes("substack.com"))
      return { text: "Substack Newsletter", url };
    if (url.includes("wordpress.com")) return { text: "WordPress Blog", url };
    if (url.includes("ghost.io")) return { text: "Ghost Blog", url };
    if (url.includes("wix.com")) return { text: "Wix Site", url };
    if (url.includes("squarespace.com"))
      return { text: "Squarespace Site", url };

    // Video Platforms
    if (url.includes("youtube.com") || url.includes("youtu.be"))
      return { text: "YouTube Video", url };
    if (url.includes("vimeo.com")) return { text: "Vimeo Video", url };
    if (url.includes("twitch.tv")) return { text: "Twitch Channel", url };
    if (url.includes("dailymotion.com"))
      return { text: "Dailymotion Video", url };
    if (url.includes("loom.com")) return { text: "Loom Recording", url };

    // File Sharing
    if (url.includes("dropbox.com")) return { text: "Dropbox File", url };
    if (url.includes("box.com")) return { text: "Box File", url };
    if (url.includes("wetransfer.com")) return { text: "WeTransfer", url };
    if (url.includes("mega.nz")) return { text: "MEGA File", url };
    if (url.includes("mediafire.com"))
      return { text: "MediaFire Download", url };

    // Learning Platforms
    if (url.includes("coursera.org")) return { text: "Coursera Course", url };
    if (url.includes("udemy.com")) return { text: "Udemy Course", url };
    if (url.includes("pluralsight.com"))
      return { text: "Pluralsight Course", url };
    if (url.includes("skillshare.com"))
      return { text: "Skillshare Class", url };
    if (url.includes("edx.org")) return { text: "edX Course", url };
    if (url.includes("brilliant.org")) return { text: "Brilliant Course", url };

    // Job Platforms
    if (url.includes("indeed.com")) return { text: "Indeed Job", url };
    if (url.includes("glassdoor.com")) return { text: "Glassdoor", url };
    if (url.includes("lever.co")) return { text: "Lever Job", url };
    if (url.includes("greenhouse.io")) return { text: "Greenhouse Job", url };
    if (url.includes("workday.com")) return { text: "Workday", url };

    // Music & Audio
    if (url.includes("spotify.com")) return { text: "Spotify", url };
    if (url.includes("apple.com/music")) return { text: "Apple Music", url };
    if (url.includes("soundcloud.com")) return { text: "SoundCloud", url };
    if (url.includes("mixcloud.com")) return { text: "Mixcloud", url };
    if (url.includes("anchor.fm")) return { text: "Anchor Podcast", url };

    // Support & Help
    if (url.includes("zendesk.com")) return { text: "Zendesk", url };
    if (url.includes("intercom.com")) return { text: "Intercom", url };
    if (url.includes("freshdesk.com")) return { text: "Freshdesk", url };
    if (url.includes("help.com")) return { text: "Help Center", url };

    // Payment & Finance
    if (url.includes("paypal.com")) return { text: "PayPal", url };
    if (url.includes("stripe.com")) return { text: "Stripe", url };
    if (url.includes("wise.com")) return { text: "Wise Transfer", url };
    if (url.includes("coinbase.com")) return { text: "Coinbase", url };

    // E-commerce
    if (url.includes("shopify.com")) return { text: "Shopify Store", url };
    if (url.includes("etsy.com")) return { text: "Etsy Shop", url };
    if (url.includes("amazon.com")) return { text: "Amazon Product", url };
    if (url.includes("ebay.com")) return { text: "eBay Listing", url };

    // Default case: Check for context or use simple format
    const contextMatch = contextBefore.match(
      /(?:visit|check|at|on|here|link to|view|see|find|contact)(?:\s+at|\s+on|\s*:\s*)?$/i
    );
    if (contextMatch) {
      return { text: "here", url };
    }

    // For unknown URLs, try to make them readable
    try {
      const urlObj = new URL(url);
      const domain = urlObj.hostname.replace("www.", "");
      return {
        text: `Visit ${domain}`,
        url,
      };
    } catch {
      return { text: "Visit Link", url };
    }
  };

  const getHref = (url: string) => {
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    if (url.startsWith("www.")) {
      return `https://${url}`;
    }
    if (url.startsWith("/")) {
      return url;
    }
    return `https://${url}`;
  };

  return (
    <div
      key={idx}
      style={{
        color: styleConfig?.Chat?.RecieverMessege?.color,
        margin: "0",
        fontSize: "14px",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {processText(text).map((segment, i) => {
        switch (segment.type) {
          case "text":
            return <span key={i}>{segment.content}</span>;

          case "link":
            return (
              <div
                key={i}
                style={{
                  display: "block",
                  position: "relative",
                }}
              >
                <a
                  href={getHref(segment.url!)}
                  target={segment.url?.startsWith("/") ? "_self" : "_blank"}
                  rel={segment.url?.startsWith("/") ? undefined : "noopener noreferrer"}
                  title={segment.url}
                  style={{
                    color: QuestThemeData?.accentColor || "#6563FF",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontSize: "14px",
                    margin: "0px 0px 5px 0px",
                    padding: "0 2px",
                    wordBreak: "break-all",
                    position: "relative",
                    transition: "all 0.2s ease",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                  onMouseEnter={(e) => {
                    const target = e.currentTarget;
                    target.style.opacity = "0.8";
                    const tooltip = target.nextElementSibling as HTMLElement;
                    if (tooltip) {
                      tooltip.style.opacity = "1";
                      tooltip.style.visibility = "visible";
                    }
                  }}
                  onMouseLeave={(e) => {
                    const target = e.currentTarget;
                    target.style.opacity = "1";
                    // Hide custom tooltip
                    const tooltip = target.nextElementSibling as HTMLElement;
                    if (tooltip) {
                      tooltip.style.opacity = "0";
                      tooltip.style.visibility = "hidden";
                    }
                  }}
                >
                  {segment.text}
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </a>

                <div
                  style={{
                    position: "absolute",
                    bottom: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                    color: "white",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                    opacity: 0,
                    visibility: "hidden",
                    transition: "all 0.2s ease",
                    zIndex: 1000,
                    marginBottom: "8px",
                    maxWidth: "300px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {segment.url}
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      borderWidth: "4px",
                      borderStyle: "solid",
                      borderColor:
                        "rgba(0, 0, 0, 0.8) transparent transparent transparent",
                    }}
                  />
                </div>
              </div>
            );

          case "bold":
            return <strong key={i}>{segment.content}</strong>;

          case "italic":
            return <em key={i}>{segment.content}</em>;

          case "break":
            return <br key={i} />;

          case "paragraph":
            return (
              <div
                key={i}
                style={{
                  height: "1em",
                  width: "100%",
                }}
              />
            );

          default:
            return null;
        }
      })}
    </div>
  );
};
